import { useState, useSyncExternalStore, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useAtajo } from '@/lib/atajos'
import { DATOS_SIMULADOS } from '@/api/origen'
import { sesionActual, suscribirse, type AccesoContribuyente } from '@/dominio/sesion/sesion'
import { FranjaSoporte } from '@/consola/FranjaSoporte'
import { Banner } from '@/ui/Banner'
import { AvisoCuenta, GuardaCuenta } from './AvisoCuenta'
import { BarraLateral } from './BarraLateral'
import { BarraSuperior, type TenantResumen } from './BarraSuperior'
import { PanelAtajos } from './PanelAtajos'
import { filasDelSelector, useCambiarDeSesion } from './selectorDeSesion'
import type { EstadoDGII } from './IndicadorDGII'

/**
 * El esqueleto:
 *
 *   barra superior de 56px
 *   barra lateral de 220px, colapsable a 56px
 *   área de contenido **sin ancho máximo**: las tablas usan toda la pantalla
 *
 * Aquí había cuatro contribuyentes escritos a mano —Ferretería Sánchez y
 * compañía— con sus semáforos puestos. Eran de muestra, y la franja ámbar de
 * abajo existía justo para que nadie los confundiera con los de verdad. El día
 * que `api/origen.ts` pasó a la red, **la franja se apagó sola y la muestra se
 * quedó**: el aviso se retiró y dejó dentro lo que vigilaba. Ahora la lista
 * sale de la sesión, que es de donde tenía que salir desde el principio.
 */

/** Proyecta un contribuyente de la sesión a la fila del selector. */
function aFilaDeSelector(acceso: AccesoContribuyente): TenantResumen {
  return {
    id: acceso.tenant,
    nombre: acceso.razonSocial,
    rnc: acceso.rnc,
    // `neutral`, no `verde`. El semáforo se calcula con los rechazos sin
    // resolver, el vencimiento del certificado y el agotamiento de la
    // secuencia, y ninguna de esas tres cosas se consulta todavía. Pintar
    // verde sería afirmar «todo en orden» sin haberlo mirado, que es el mismo
    // error que inventarse el nombre.
    semaforo: 'neutral',
  }
}

/** Dos letras para el avatar. El servidor no manda nombre para mostrar, solo el correo. */
function inicialesDe(email: string): string {
  const local = email.split('@')[0] ?? email
  const partes = local.split(/[._-]/).filter(Boolean)
  const letras = partes.length > 1 ? partes[0]![0]! + partes[1]![0]! : local.slice(0, 2)
  return letras.toUpperCase()
}

export function LayoutApp({ children }: { children: ReactNode }) {
  const navegar = useNavigate()
  const [atajosAbiertos, setAtajosAbiertos] = useState(false)
  // En el teléfono la barra lateral se abre encima (BarraLateral).
  const [menuAbierto, setMenuAbierto] = useState(false)
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)

  // Estado del servicio de DGII. Llegará por el canal de eventos (E27).
  const [estadoDGII] = useState<EstadoDGII>('normal')

  // Los despachos de la cuenta van en la misma lista, arriba: se pasa a su
  // cartera con el mismo Ctrl+K (docs/adr/0021).
  const tenants = sesion ? filasDelSelector(sesion) : []
  const tenantActivo = sesion?.activo ? aFilaDeSelector(sesion.activo) : null
  const cambio = useCambiarDeSesion()

  useAtajo({ tecla: '?', shift: true }, () => setAtajosAbiertos(true))
  useAtajo({ tecla: 'n' }, () => {
    void navegar({ to: '/emitir' })
  })

  return (
    <div className="flex min-h-screen flex-col bg-gris-50">
      {/* Modo soporte: un operador dentro de la cuenta del cliente (docs/09 §4). */}
      <FranjaSoporte />

      {/*
        Franja de ambiente. Separación visual total, igual que el modo de
        pruebas: **confundir datos reales con datos de ejemplo es un error caro
        y se evita con color, no con disciplina.** Desaparece sola el día que
        `api/origen.ts` apunte a la red.
      */}
      {DATOS_SIMULADOS ? (
        <div
          role="status"
          className="flex h-6 shrink-0 items-center justify-center gap-2 bg-ambar text-white sin-impresion"
        >
          <span className="text-etiqueta font-semibold uppercase tracking-wide">Datos de ejemplo</span>
          <span className="text-etiqueta opacity-90">
            los extremos de negocio todavía no existen · la frontera está en src/api/origen.ts
          </span>
        </div>
      ) : null}

      {sesion && tenantActivo ? (
        <BarraSuperior
          tenants={tenants}
          tenantActivo={tenantActivo}
          // Cambiar de contribuyente **no es cambiar una variable**: el tenant
          // viaja dentro del testigo firmado, así que se pide uno nuevo con el
          // vigente (`POST /interna/sesion/cambiar`), sin volver a teclear la
          // credencial. Fingir el cambio en el cliente pintaría un nombre
          // mientras las consultas siguen corriendo bajo el tenant anterior.
          onCambiarTenant={(id) => {
            if (id !== tenantActivo.id && !cambio.cambiando) void cambio.cambiar(id)
          }}
          estadoDGII={estadoDGII}
          usuario={{ nombre: sesion.email, iniciales: inicialesDe(sesion.email) }}
          onAbrirMenu={() => setMenuAbierto(true)}
        />
      ) : null}

      {cambio.error !== null ? (
        <Banner tono="rojo" titulo="No se cambió de contribuyente" onCerrar={cambio.limpiarError}>
          {cambio.error}
        </Banner>
      ) : null}

      {/* La cuenta con Agentical Group: por vencer, en gracia, restringida o suspendida (docs/adr/0020). */}
      {sesion ? <AvisoCuenta /> : null}

      <div className="flex min-h-0 flex-1">
        <BarraLateral abiertaEnMovil={menuAbierto} alCerrarEnMovil={() => setMenuAbierto(false)} />
        {/* Sin ancho máximo: las tablas usan toda la pantalla. */}
        <main className="area-contenido min-w-0 flex-1 p-4 md:p-6">
          <GuardaCuenta>{children}</GuardaCuenta>
        </main>
      </div>

      <PanelAtajos abierto={atajosAbiertos} onCerrar={() => setAtajosAbiertos(false)} />
    </div>
  )
}
